/**
 * Non-blocking, best-effort delivery of a completed client performance attempt
 * to the AdminAPI `clientperformance` endpoint. Never throws and never blocks
 * calculator bootstrap — one keepalive `fetch`, then exactly one retry.
 *
 * WHY NOT `navigator.sendBeacon` (deviation from the spec's "prefer
 * sendBeacon"): a beacon is always sent with credentials mode `include`. The
 * AdminAPI answers CORS for this public endpoint with
 * `Access-Control-Allow-Origin: *`, and a wildcard origin is illegal for a
 * credentialed request, so Chrome rejects the preflight
 * ("The value of the 'Access-Control-Allow-Origin' header in the response must
 * not be the wildcard '*' when the request's credentials mode is 'include'"),
 * the POST fails with net::ERR_FAILED and no row is ever created. Worse,
 * `sendBeacon` returns `true` for a merely QUEUED request, so the failure is
 * invisible to us and no fallback ever runs. Making beacons legal would force
 * the server to echo arbitrary storefront origins AND allow credentials, which
 * we do not want. `fetch(..., { credentials: "omit", mode: "cors" })` is
 * compatible with the wildcard header, keeps `keepalive: true` for unload-time
 * delivery, and reports its own failures so the retry ladder works.
 *
 * Dependency-free (no bootstrap/appState imports) so this module cannot create
 * a circular import; the caller resolves shop and endpoint.
 */
import type { ClientPerformanceAttemptPayload } from "./client-performance-tracker";

const MILESTONE_FIELDS: Record<string, keyof ClientPerformanceReportRequest> = {
  embed_seen: "embedSeenMs",
  embed_boot: "embedBootMs",
  jquery_ready: "jqueryReadyMs",
  bundle_requested: "bundleRequestedMs",
  initialization_started: "initializationStartedMs",
  loader_started: "loaderStartedMs",
  bundle_ready: "bundleReadyMs",
  configuration_ready: "configurationReadyMs",
  mount_target_ready: "mountTargetReadyMs",
  calculator_visible: "calculatorVisibleMs",
  calculator_interactive: "calculatorInteractiveMs",
  terminal: "terminalMs",
};

export interface ClientPerformanceReportRequest {
  shop: string;
  attemptId: string;
  schemaVersion: 1;
  normalizedUrl: string;
  runtimePath: "legacy" | "v3";
  calculatorId?: string;
  calculatorName?: string;
  calculatorVersion?: string;
  result: ClientPerformanceAttemptPayload["result"];
  failureReason?: string;
  slow: boolean;
  deviceClass: ClientPerformanceAttemptPayload["deviceClass"];
  browserFamily: string;
  browserMajorVersion?: number;
  osFamily: string;
  effectiveNetworkType?: string;
  saveData?: boolean;
  viewportBucket: string;
  navigationType: string;
  wasHidden?: boolean;
  payloadSource?: string;
  clientTimestamp: string;
  embedSeenMs?: number;
  embedBootMs?: number;
  jqueryReadyMs?: number;
  bundleRequestedMs?: number;
  initializationStartedMs?: number;
  loaderStartedMs?: number;
  bundleReadyMs?: number;
  configurationReadyMs?: number;
  mountTargetReadyMs?: number;
  calculatorVisibleMs?: number;
  calculatorInteractiveMs?: number;
  terminalMs?: number;
}

export function buildClientPerformanceReport(payload: ClientPerformanceAttemptPayload, shop: string): ClientPerformanceReportRequest {
  const report: ClientPerformanceReportRequest = {
    shop,
    attemptId: payload.attemptId,
    schemaVersion: payload.schemaVersion,
    normalizedUrl: payload.normalizedUrl,
    runtimePath: payload.runtimePath,
    result: payload.result,
    slow: payload.slow,
    deviceClass: payload.deviceClass,
    browserFamily: payload.browserFamily,
    osFamily: payload.osFamily,
    viewportBucket: payload.viewportBucket,
    navigationType: payload.navigationType,
    wasHidden: payload.wasHidden,
    clientTimestamp: payload.clientTimestamp,
  };
  if (payload.calculatorId !== undefined) report.calculatorId = payload.calculatorId;
  if (payload.calculatorName !== undefined) report.calculatorName = payload.calculatorName;
  if (payload.calculatorVersion !== undefined) report.calculatorVersion = payload.calculatorVersion;
  if (payload.failureReason !== undefined) report.failureReason = payload.failureReason;
  if (payload.browserMajorVersion !== undefined) report.browserMajorVersion = payload.browserMajorVersion;
  if (payload.effectiveNetworkType !== undefined) report.effectiveNetworkType = payload.effectiveNetworkType;
  if (payload.saveData !== undefined) report.saveData = payload.saveData;
  if (payload.payloadSource !== undefined) report.payloadSource = payload.payloadSource;

  for (const [milestone, field] of Object.entries(MILESTONE_FIELDS)) {
    const value = payload.timings[milestone as keyof ClientPerformanceAttemptPayload["timings"]];
    if (value !== undefined) (report as unknown as Record<string, number>)[field] = value;
  }
  return report;
}

export function resolveClientPerformanceEndpoint(baseApiUrl: string): string {
  return `${baseApiUrl}clientperformance`;
}

async function postOnce(endpoint: string, body: string): Promise<boolean> {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    // Anonymous by design: the endpoint answers with a wildcard
    // Access-Control-Allow-Origin, which browsers reject for any credentialed
    // request. See the module comment.
    credentials: "omit",
    mode: "cors",
  });
  return Boolean(response?.ok);
}

/** How the single delivery attempt for a page attempt ended. */
export type ClientPerformanceDeliveryOutcome = "fetch" | "retry" | "failed";

export async function reportClientPerformanceAttempt(
  payload: ClientPerformanceAttemptPayload,
  shop: string,
  endpoint: string,
): Promise<ClientPerformanceDeliveryOutcome> {
  try {
    const body = JSON.stringify(buildClientPerformanceReport(payload, shop));

    try {
      if (await postOnce(endpoint, body)) return "fetch";
    } catch {
      // first fetch rejected — fall through to the single retry.
    }

    try {
      if (await postOnce(endpoint, body)) return "retry";
      return "failed";
    } catch {
      return "failed";
    }
  } catch {
    return "failed";
  }
}
